/**
 * The months a demand's Monthly Breakdown covers.
 *
 * A role budget keeps the months it was first spread over: shortening the
 * project in EBProjects leaves hours keyed for months the demand no longer
 * asks for, and the demand itself is already trimmed by the time it reaches the
 * scheduler. These helpers derive the demand's own window so the breakdown can
 * show its months rather than every month the stored budget carries.
 */

import type { ResourceRequest } from '@/types/resourceRequest';

/** A 'YYYY-MM' range, either end inclusive and either end optionally unbounded. */
export interface MonthWindow {
  start?: string;
  end?: string;
}

/** 'YYYY-MM' of an ISO date, or undefined when there is no usable date. */
export function monthKeyOf(isoDate: string | null | undefined): string | undefined {
  if (!isoDate) return undefined;
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** True when a 'YYYY-MM' month falls inside a window. Ordinal 'YYYY-MM' compare. */
export function isMonthInWindow(month: string, window: MonthWindow): boolean {
  if (window.start && month < window.start) return false;
  if (window.end && month > window.end) return false;
  return true;
}

type DemandWindowFields = Pick<
  ResourceRequest,
  'requestedStartDate' | 'requestedEndDate' | 'monthlyHourHints' | 'monthlyAllocatedHours'
>;

/**
 * The months a demand runs for, from its requested dates widened to any month it
 * asks for or already has bookings in — those are real work and must stay in
 * range even when they sit outside the requested dates.
 *
 * Either end is absent when nothing gives it, and is then unbounded: a demand
 * with no usable dates and no monthly figures keeps every month it is shown
 * against rather than reading as an empty window.
 */
export function demandMonthWindow(request: DemandWindowFields): MonthWindow {
  const knownMonths = [
    ...Object.keys(request.monthlyHourHints ?? {}),
    ...Object.keys(request.monthlyAllocatedHours ?? {}),
  ].sort();

  const startCandidates = [monthKeyOf(request.requestedStartDate), knownMonths[0]]
    .filter((m): m is string => !!m);
  const endCandidates = [monthKeyOf(request.requestedEndDate), knownMonths[knownMonths.length - 1]]
    .filter((m): m is string => !!m);

  const window: MonthWindow = {};
  if (startCandidates.length > 0) window.start = startCandidates.reduce((a, b) => (a < b ? a : b));
  // An end is only bounded when the demand states one: a demand with no end date
  // runs until it is filled, so the months it is planned into are not its last.
  if (monthKeyOf(request.requestedEndDate) && endCandidates.length > 0) {
    window.end = endCandidates.reduce((a, b) => (a > b ? a : b));
  }
  return window;
}

/**
 * The months the Monthly Breakdown renders: every month the role budget, the
 * demand or its bookings reference, less the months that fall outside the
 * project's or the demand's window.
 *
 * A month the demand asks for or already has bookings in survives either way —
 * it is real work, and hiding it would hide that it sits outside those windows.
 * Only budget-only months are dropped.
 */
export function monthlyBreakdownMonths(args: {
  monthlyBudget: Record<string, number>;
  demandHints: Record<string, number>;
  monthlyBooked: Record<string, number>;
  projectWindow: MonthWindow;
  demandWindow: MonthWindow;
}): string[] {
  const { monthlyBudget, demandHints, monthlyBooked, projectWindow, demandWindow } = args;
  return Array.from(
    new Set([...Object.keys(monthlyBudget), ...Object.keys(demandHints), ...Object.keys(monthlyBooked)]),
  )
    .filter((month) =>
      (isMonthInWindow(month, projectWindow) && isMonthInWindow(month, demandWindow))
      || (demandHints[month] ?? 0) > 0
      || (monthlyBooked[month] ?? 0) > 0)
    .sort();
}

/**
 * The months the Monthly Breakdown renders against the LIVE project picture:
 * every month the role budget, the demand or its bookings reference, less the
 * budget-only months that fall outside the project's live window.
 *
 * Unlike `monthlyBreakdownMonths` this does not clip against the demand's own
 * window, which is the snapshot taken when the demand was raised: extending the
 * project in EBProjects and budgeting the new months has to show those months
 * here, without the demand being declined and re-raised. Shortening the project
 * still hides the months the budget kept, because the live project window is
 * what the filter reads.
 *
 * A month the demand asks for or already has bookings in survives regardless —
 * it is real work, and hiding it would hide that it sits outside the window.
 * An unbounded window (the project's dates are unknown) keeps every month, so
 * the breakdown never reads as empty.
 */
export function liveBreakdownMonths(args: {
  monthlyBudget: Record<string, number>;
  demandHints: Record<string, number>;
  monthlyBooked: Record<string, number>;
  projectWindow: MonthWindow;
}): string[] {
  const { monthlyBudget, demandHints, monthlyBooked, projectWindow } = args;
  return Array.from(
    new Set([...Object.keys(monthlyBudget), ...Object.keys(demandHints), ...Object.keys(monthlyBooked)]),
  )
    .filter((month) =>
      isMonthInWindow(month, projectWindow)
      || (demandHints[month] ?? 0) > 0
      || (monthlyBooked[month] ?? 0) > 0)
    .sort();
}

/**
 * The live monthly spread to display: the role budget's month hours restricted
 * to the months the project currently runs for, dropping empty months.
 *
 * This is the budgeted picture as it stands now rather than the hours the
 * demand froze when it was raised, so budget added for months the project was
 * extended into shows, and budget removed stops showing.
 */
export function liveMonthlySpread(
  monthlyBudget: Record<string, number>,
  projectWindow: MonthWindow,
): Record<string, number> {
  return Object.fromEntries(
    Object.entries(monthlyBudget)
      .filter(([month, hours]) => hours > 0 && isMonthInWindow(month, projectWindow))
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

/**
 * The month the live picture starts in: the project's own start, or the first
 * month it budgets or is planned into when the project's dates are unknown.
 * Undefined when nothing gives a live month, leaving the demand's own requested
 * start as the only thing to show.
 */
export function liveStartMonth(
  projectWindow: MonthWindow,
  ...monthSources: Record<string, number>[]
): string | undefined {
  if (projectWindow.start) return projectWindow.start;
  const months = monthSources
    .flatMap((source) => Object.keys(source))
    .filter((month) => isMonthInWindow(month, projectWindow))
    .sort();
  return months[0];
}

/**
 * The date to show as a demand's start: the date it was raised for in
 * EBProjects, which is what the Open Demand list shows.
 *
 * The project's own start month is only a stand-in for demands raised before a
 * requested date was captured — pinned to the 1st, it would otherwise read as a
 * day the demand never asked for.
 */
export function summaryStartDate(
  requestedStartDate: string | null | undefined,
  liveStart: string | undefined,
): string {
  if (requestedStartDate) return requestedStartDate;
  return liveStart ? `${liveStart}-01` : '';
}
